import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { Brief, Field as FieldDef, SpaceOut, TextFormat } from "../../api/types.gen.ts";
import { type Key, locale, t } from "../../i18n/index.ts";
import { useListView } from "../../lib/cardView.ts";
import { optionLabel } from "../../lib/fieldTypes.ts";
import { CURRENCIES } from "../../lib/format.ts";
import {
  BR_STATES,
  checkOf,
  type Kind,
  kindOf,
  mapUrl,
  maskOf,
  parseLocation,
  placeholderOf,
  pointOf,
  readField,
  roundCoord,
  showLocation,
  UNITS,
  unitSymbol,
} from "../../lib/formats.ts";
import { decimalInput, decimalParser } from "../../lib/number.ts";
import { outsideOf, spaceUrl } from "../../lib/paths.ts";
import { useAction } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { useValueText } from "../../lib/textState.ts";
import { IconButton, InlineButton, Link } from "../../ui/Button.tsx";
import { Chip } from "../../ui/Chip.tsx";
import { Combobox } from "../../ui/Combobox.tsx";
import { Fold } from "../../ui/Fold.tsx";
import { Checkbox, Input, Select, Textarea } from "../../ui/form.tsx";
import { AddLinkIcon, CardsViewIcon, CloseIcon, SearchIcon, TableViewIcon, UploadIcon } from "../../ui/icons.tsx";
import { CONTROL_FRAME, iconButtonClass } from "../../ui/styles.ts";
import { searchItems } from "../items/search.tsx";
import { LinkedItems } from "./linkedItems.tsx";
import { TemporalInput } from "./temporalInputs.tsx";

type OnChange = (v: unknown) => void;

/** Locale-aware decimal text box (inputmode=decimal); reports canonical strings ("1234.5"), `undefined` when empty. */
function DecimalInput(p: {
  value: string | number | undefined;
  onChange: (canonical: string | undefined) => void;
  label?: string | undefined;
}) {
  const lang = locale.value;
  const parse = (s: string) => (s.trim() === "" ? undefined : decimalParser(lang)(s));
  const [text, setText] = useValueText(
    p.value,
    (v) => (v === undefined ? "" : decimalInput(v, lang)),
    (s, v) => {
      const d = parse(s);
      return d === undefined ? v === undefined : d !== null && v !== undefined && Number(d) === Number(v);
    },
  );
  return (
    <Input
      inputMode="decimal"
      autocomplete="off"
      aria-label={p.label}
      value={text}
      aria-invalid={parse(text) === null || undefined}
      onValue={(s) => {
        setText(s);
        const d = parse(s);
        if (d !== null) {
          p.onChange(d);
        }
      }}
    />
  );
}

/** JSON text, parsed (`undefined` when empty); throws when it isn't JSON. */
const parseJson = (s: string): unknown => (s.trim() === "" ? undefined : JSON.parse(s));

/** Whether `s` is JSON (or empty). */
function isJson(s: string): boolean {
  try {
    parseJson(s);
    return true;
  } catch {
    return false;
  }
}

/** JSON text box for `any` values (and off-schema data). */
function JsonInput(p: { value: unknown; onChange: OnChange; label?: string | undefined }) {
  const [text, setText] = useValueText(
    p.value,
    (v) => (v === undefined ? "" : JSON.stringify(v, null, 2)),
    (s, v) => isJson(s) && JSON.stringify(parseJson(s)) === JSON.stringify(v),
  );
  return (
    <Textarea
      class="font-mono text-xs"
      aria-label={p.label}
      value={text}
      aria-invalid={!isJson(text) || undefined}
      spellcheck={false}
      onValue={(s) => {
        setText(s);
        if (isJson(s)) {
          p.onChange(parseJson(s));
        }
      }}
    />
  );
}

function MoneyInput({ field, value, onChange }: { field: FieldDef; value: unknown; onChange: OnChange }) {
  const m = (typeof value === "object" && value !== null ? value : {}) as { amount?: unknown; currency?: unknown };
  const amount = typeof m.amount === "string" ? m.amount : undefined;
  const currency = typeof m.currency === "string" ? m.currency : (field.currency ?? "");
  // A fixed currency can't be changed, unless a stored value is in another one and needs fixing.
  const locked = Boolean(field.fixed_currency) && currency === field.currency;
  const emit = (a: string | undefined, c: string) => onChange(a === undefined ? undefined : { amount: a, currency: c });
  return (
    <div class="flex gap-2">
      <DecimalInput value={amount} label={t("fields.amount")} onChange={(a) => emit(a, currency)} />
      <Select
        class="w-28"
        aria-label={t("fields.currency")}
        disabled={locked}
        title={locked ? t("fields.currencyFixed") : undefined}
        value={currency}
        onValue={(c) => emit(amount, c)}
        options={[{ value: "", label: "—" }, ...CURRENCIES.map((c) => ({ value: c, label: c }))]}
      />
    </div>
  );
}

const str = (v: unknown) => (typeof v === "string" ? v : "");

/** How each typed kind asks the keyboard for its characters. */
const INPUT_MODES: Partial<Record<Kind, { type?: string; inputMode?: "email" | "tel" | "url" | "numeric" }>> = {
  email: { type: "email", inputMode: "email" },
  phone: { type: "tel", inputMode: "tel" },
  url: { inputMode: "url" },
  br_cpf: { inputMode: "numeric" },
  br_cep: { inputMode: "numeric" },
  ean: { inputMode: "numeric" },
  br_ncm: { inputMode: "numeric" },
  br_cest: { inputMode: "numeric" },
  br_cfop: { inputMode: "numeric" },
  br_nfe_key: { inputMode: "numeric" },
  br_boleto: { inputMode: "numeric" },
  br_pis: { inputMode: "numeric" },
  br_renavam: { inputMode: "numeric" },
  it_partita_iva: { inputMode: "numeric" },
  pt_nif: { inputMode: "numeric" },
  fr_siren: { inputMode: "numeric" },
  fr_siret: { inputMode: "numeric" },
  uk_sort_code: { inputMode: "numeric" },
  uk_utr: { inputMode: "numeric" },
  us_ein: { inputMode: "numeric" },
  us_zip: { inputMode: "numeric" },
  us_routing_number: { inputMode: "numeric" },
  au_abn: { inputMode: "numeric" },
  au_acn: { inputMode: "numeric" },
  nz_ird: { inputMode: "numeric" },
  imei: { inputMode: "numeric" },
  microchip: { inputMode: "numeric" },
  hs_code: { inputMode: "numeric" },
};

/** Under a typed value: how it reads (its display form, a Pix key's kind), or that it isn't valid. */
function ReadBack({ field, kind, value }: { field: FieldDef; kind: Kind; value: string }) {
  if (value.trim() === "") {
    return null;
  }
  const region = field.region ?? null;
  const r = readField(field, kind, value, region);
  const verdict =
    kind === "custom" ? (r?.valid ? "valid" : "invalid") : checkOf(kind, value, region, field.strict ?? false);
  if (verdict !== "valid" || !r) {
    const what = kind === "email" || kind === "phone" || kind === "url" ? t(`type.${kind}`) : null;
    const message =
      verdict === "international"
        ? t("fields.notInternational")
        : kind === "custom"
          ? t("fields.notMatching")
          : t("fields.notValid", { format: what ?? t(`format.${kind as TextFormat}`) });
    return <p class="text-xs text-amber-700">{message}</p>;
  }
  const pix = kind === "br_pix_key" && r.kind ? t(`pixKind.${r.kind}` as Key) : null;
  // An IE read without a state names the state that took it.
  const state = kind === "br_ie" && r.kind ? BR_STATES[r.kind] : undefined;
  const shownAs = r.display === value.trim() ? null : r.display;
  const notes = [pix, state, shownAs].filter(Boolean);
  return notes.length > 0 ? <p class="text-xs text-zinc-500">{notes.join(" · ")}</p> : null;
}

/** A typed or formatted text (email, phone, URL, CPF, IBAN…): its keyboard, an example, a mask where it has one. */
function FormattedInput({ field, kind, value, onChange, label }: ScalarProps & { kind: Kind }) {
  const text = str(value);
  return (
    <div class="flex flex-col gap-1">
      <Input
        aria-label={label}
        autocomplete="off"
        spellcheck={false}
        {...INPUT_MODES[kind]}
        placeholder={placeholderOf(field, kind, field.region)}
        value={text}
        // Typing forward fills the separators in; deleting leaves what's there.
        onValue={(v) => onChange((v.length > text.length ? maskOf(field, kind, v) : v) || undefined)}
      />
      <ReadBack field={field} kind={kind} value={text} />
    </div>
  );
}

/** The point a location value, or the text typed for one, reads as. */
function pointIn(v: unknown) {
  return pointOf(v) ?? (typeof v === "string" ? parseLocation(v) : null);
}

/**
 * A location: its coordinates typed as `lat, lng` (or in degrees, minutes and seconds), stored as `{lat, lng}`, a
 * label beside them, and a link to the place on OpenStreetMap.
 */
function LocationInput({ value, onChange, label }: ScalarProps) {
  const p = pointOf(value);
  const [text, setText] = useValueText(
    value,
    (v) => {
      const q = pointOf(v);
      return q ? showLocation(q) : typeof v === "string" ? v : "";
    },
    (s, v) => {
      const [a, b] = [parseLocation(s), pointIn(v)];
      return s.trim() === "" ? v === undefined : a !== null && b !== null && a.lat === b.lat && a.lng === b.lng;
    },
  );
  const typed = text.trim() === "" ? null : parseLocation(text);
  const emit = (q: { lat: number; lng: number } | null, name: string | undefined) =>
    onChange(q ? { lat: roundCoord(q.lat), lng: roundCoord(q.lng), ...(name && { label: name }) } : undefined);
  return (
    <div class="flex flex-col gap-1">
      <div class="flex gap-2">
        <Input
          aria-label={label}
          autocomplete="off"
          spellcheck={false}
          placeholder="-23.5505, -46.6333"
          title={t("fields.locationHint")}
          value={text}
          aria-invalid={(text.trim() !== "" && typed === null) || undefined}
          onValue={(s) => {
            setText(s);
            if (s.trim() === "") {
              onChange(undefined);
            } else {
              const q = parseLocation(s);
              if (q) {
                emit(q, p?.label);
              }
            }
          }}
        />
        <Input
          class="w-48"
          aria-label={t("fields.locationLabel")}
          placeholder={t("fields.locationLabel")}
          value={p?.label ?? ""}
          disabled={!p}
          onValue={(name) => emit(p, name.trim() || undefined)}
        />
      </div>
      {text.trim() !== "" && typed === null ? (
        <p class="text-xs text-amber-700">{t("fields.notLocation")}</p>
      ) : p ? (
        <a
          class="self-start text-xs text-blue-700 hover:underline"
          href={mapUrl(p)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("fields.openMap")}
        </a>
      ) : null}
    </div>
  );
}

/** The percent sign beside a percentage. */
const PERCENT = "%";

/** A percentage: the number of percent (12.5 is 12.5%). */
function PercentageInput({ value, onChange, label }: ScalarProps) {
  return (
    <div class="flex items-center gap-2">
      <DecimalInput
        label={label}
        value={typeof value === "number" ? value : undefined}
        onChange={(d) => onChange(d === undefined ? d : Number(d))}
      />
      <span class="text-sm text-zinc-500">{PERCENT}</span>
    </div>
  );
}

/** A quantity: an amount and a unit, among the field's (its fixed one, its allowed ones, else any). */
function QuantityInput({ field, value, onChange }: ScalarProps) {
  const q = (typeof value === "object" && value !== null ? value : {}) as { amount?: unknown; unit?: unknown };
  const amount = typeof q.amount === "string" ? q.amount : undefined;
  const unit = typeof q.unit === "string" ? q.unit : (field.unit ?? field.units?.[0] ?? "");
  const allowed = field.unit ? [field.unit] : field.units?.length ? field.units : UNITS.map(([u]) => u);
  // A stored value in another unit stays offered, to be seen and fixed.
  const offered = unit && !allowed.includes(unit) ? [...allowed, unit] : allowed;
  const emit = (a: string | undefined, u: string) => onChange(a === undefined ? undefined : { amount: a, unit: u });
  return (
    <div class="flex gap-2">
      <DecimalInput value={amount} label={t("fields.amount")} onChange={(a) => emit(a, unit)} />
      <Select
        class="w-28"
        aria-label={t("fields.unit")}
        disabled={Boolean(field.unit) && unit === field.unit}
        value={unit}
        onValue={(u) => emit(amount, u)}
        options={[
          ...(unit ? [] : [{ value: "", label: "—" }]),
          ...offered.map((u) => ({ value: u, label: unitSymbol(u) })),
        ]}
      />
    </div>
  );
}

/** A `many` select as a group of checkboxes. */
function MultiSelect({ field, value, onChange }: { field: FieldDef; value: unknown[]; onChange: OnChange }) {
  const opts = [...new Set([...(field.options ?? []), ...value.filter((v): v is string => typeof v === "string")])];
  return (
    <div class="flex flex-wrap gap-x-4 gap-y-1">
      {opts.map((o) => (
        <Checkbox
          key={o}
          label={optionLabel(field, o)}
          checked={value.includes(o)}
          onCheck={(on) => {
            const next = on ? [...value, o] : value.filter((v) => v !== o);
            onChange(next.length > 0 ? next : undefined);
          }}
        />
      ))}
    </div>
  );
}

/** A `many` scalar as a list of editors. */
function ManyInput({ field, value, onChange }: { field: FieldDef; value: unknown[]; onChange: OnChange }) {
  const set = (i: number, v: unknown) => onChange(value.map((x, j) => (i === j ? v : x)));
  const one = { ...field, many: false };
  return (
    <div class="flex flex-col gap-2">
      {value.map((v, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: list positions are the identity of unkeyed values.
        <div key={i} class="flex items-start gap-2">
          <div class="flex-1">
            <ScalarInput field={one} label={t("fields.valueN", { n: i + 1 })} value={v} onChange={(x) => set(i, x)} />
          </div>
          <IconButton size="md" label={t("common.remove")} onClick={() => onChange(value.filter((_, j) => j !== i))}>
            <CloseIcon size={14} />
          </IconButton>
        </div>
      ))}
      <InlineButton tone="link" class="self-start" onClick={() => onChange([...value, undefined])}>
        {t("fields.addValue")}
      </InlineButton>
    </div>
  );
}

interface ScalarProps {
  field: FieldDef;
  value: unknown;
  onChange: OnChange;
  /** Accessible name when the control isn't labelled by a <Field>. */
  label?: string | undefined;
}

/** Editor for one value of a field (not `many`). `undefined` means "unset". */
export function ScalarInput({ field, value, onChange, label }: ScalarProps) {
  switch (field.type) {
    case "number":
      return (
        <DecimalInput
          label={label}
          value={typeof value === "number" ? value : undefined}
          onChange={(d) => onChange(d === undefined ? d : Number(d))}
        />
      );
    case "money":
      return <MoneyInput field={field} value={value} onChange={onChange} />;
    case "percentage":
      return <PercentageInput field={field} label={label} value={value} onChange={onChange} />;
    case "quantity":
      return <QuantityInput field={field} value={value} onChange={onChange} />;
    case "location":
      return <LocationInput field={field} label={label} value={value} onChange={onChange} />;
    case "boolean":
      return (
        <Select
          aria-label={label}
          value={typeof value === "boolean" ? String(value) : ""}
          onValue={(v) => onChange(v === "" ? undefined : v === "true")}
          options={[
            { value: "", label: "—" },
            { value: "true", label: t("common.yes") },
            { value: "false", label: t("common.no") },
          ]}
        />
      );
    case "date":
      return <Input type="date" aria-label={label} value={str(value)} onValue={(v) => onChange(v || undefined)} />;
    case "time":
    case "datetime":
    case "local_datetime":
    case "instant":
    case "duration":
    case "timezone":
      return <TemporalInput field={field} label={label} value={value} onChange={onChange} />;
    case "select": {
      const opts = field.options ?? [];
      const extra = typeof value === "string" && !opts.includes(value) ? [value] : [];
      return (
        <Select
          aria-label={label}
          value={str(value)}
          onValue={(v) => onChange(v || undefined)}
          options={[
            { value: "", label: "—" },
            ...[...opts, ...extra].map((o) => ({ value: o, label: optionLabel(field, o) })),
          ]}
        />
      );
    }
    case "any":
      return <JsonInput label={label} value={value} onChange={onChange} />;
    default: {
      const kind = kindOf(field.type, field.format, field);
      return kind ? (
        <FormattedInput field={field} kind={kind} label={label} value={value} onChange={onChange} />
      ) : (
        <Input aria-label={label} value={str(value)} onValue={(v) => onChange(v || undefined)} />
      );
    }
  }
}

/** Editor for a data field of any type and cardinality. */
export function ValueInput({ field, value, onChange, label }: ScalarProps) {
  if (!field.many) {
    return <ScalarInput field={field} label={label} value={value} onChange={onChange} />;
  }
  const list = Array.isArray(value) ? (value as unknown[]) : [];
  return field.type === "select" ? (
    <MultiSelect field={field} value={list} onChange={onChange} />
  ) : (
    <ManyInput field={field} value={list} onChange={onChange} />
  );
}

/** How long a search takes to grow out of its icon. */
const GROW_MS = 150;

/**
 * A search grown out of its icon, in its place (focused, its icon inside): Esc, or leaving it empty, puts the icon back.
 * Where there's no room beside what's before it, the row it's in wraps.
 */
function GrownSearch(p: { label: string; value: string; onValue: (v: string) => void; onClose: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [wide, setWide] = useState(false);
  useEffect(() => {
    ref.current?.focus();
    const frame = requestAnimationFrame(() => setWide(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <span class="relative flex max-w-full items-center">
      <SearchIcon class="pointer-events-none absolute left-1.5 text-zinc-500" />
      <input
        ref={ref}
        type="search"
        aria-label={p.label}
        placeholder={p.label}
        value={p.value}
        style={{ transitionDuration: `${GROW_MS}ms` }}
        class={`${CONTROL_FRAME} h-7 max-w-full py-0 pr-2 pl-7 text-sm transition-[width] ${wide ? "w-60" : "w-7"}`}
        onInput={(e) => p.onValue(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            p.onClose();
          }
        }}
        onBlur={() => {
          if (!p.value) {
            p.onClose();
          }
        }}
      />
    </span>
  );
}

/** Whether `title` has `query` in it, case and accents aside. */
const matches = (title: string, query: string) => {
  const plain = (x: string) => x.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase();
  return plain(title).includes(plain(query.trim()));
};

/**
 * Linked items of one refs key, under its heading (its name, `heading`), folded when several: muted icons beside it
 * narrow them by title (several), search items to link (replacing the link unless `many`), upload one, and show them
 * as a table or cards. Empty, its search for items to link is there from the start.
 */
export function RefInput(p: {
  space: SpaceOut;
  label: string;
  heading: ComponentChildren;
  ids: readonly string[];
  links: readonly Brief[];
  target?: string | null;
  many: boolean;
  onChange: (ids: string[]) => void;
  /** Offers "Upload file" next to the search; the upload is linked on the server right away. */
  onUpload?: ((files: File[]) => Promise<void>) | undefined;
}) {
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [asked, setAsked] = useState(false);
  // Its links narrowed by their titles (several): asked for with its own icon.
  const [filtering, setFiltering] = useState(false);
  const [query, setQuery] = useState("");
  // Its links as a table or as cards (remembered, as for other lists).
  const [view, setView] = useListView("links");
  const [upload, uploading] = useAction(async (files: File[]) => {
    await p.onUpload?.(files);
  });
  const brief = (id: string) => p.links.find((l) => l.id === id);
  // Read-only: its links, without ways to change them.
  const editable = canEdit(p.space);
  // A link to someone's private item shows without its title or a link.
  const title = (id: string) => (brief(id)?.hidden ? t("item.privateItem") : (titles[id] ?? brief(id)?.title ?? id));
  const chip = (id: string) => {
    if (brief(id)?.missing) {
      return <span class="text-red-700 line-through">{id}</span>;
    }
    return brief(id)?.hidden ? (
      <span class="text-zinc-500 italic">{title(id)}</span>
    ) : (
      <Link href={spaceUrl(p.space, `/items/${id}`)} class="text-blue-700 hover:underline">
        {title(id) || t("common.untitled")}
      </Link>
    );
  };
  if (!editable && p.ids.length === 0) {
    return <p class="text-sm text-zinc-400">—</p>;
  }
  const other = view === "table" ? "cards" : "table";
  // Its search: asked for with its icon, or there from the start while nothing's linked.
  const searching = editable && (asked || p.ids.length === 0);
  const shown = query && filtering ? p.ids.filter((id) => matches(title(id), query)) : p.ids;
  const linked = (
    <LinkedItems
      space={p.space}
      ids={shown}
      links={p.links}
      target={p.target ?? null}
      view={view === "table" ? "table" : "cards"}
      onRemove={editable ? (id) => p.onChange(p.ids.filter((x) => x !== id)) : undefined}
    />
  );
  // In the space, its links listed as items are (outside it, just their names).
  const listed = !outsideOf(p.space) && p.ids.length > 0;
  const tools = (
    <span class="ms-auto flex items-center gap-0.5 text-zinc-500">
      {listed && p.ids.length > 1 && filtering ? (
        <GrownSearch
          label={t("fields.searchLinked", { field: p.label })}
          value={query}
          onValue={setQuery}
          onClose={() => {
            setFiltering(false);
            setQuery("");
          }}
        />
      ) : null}
      {listed && p.ids.length > 1 && !filtering ? (
        <IconButton
          label={t("fields.searchLinked", { field: p.label })}
          aria-expanded={filtering}
          onClick={() => {
            setFiltering(!filtering);
            setQuery("");
          }}
        >
          <SearchIcon />
        </IconButton>
      ) : null}
      {editable && p.ids.length > 0 ? (
        <IconButton
          label={t("fields.linkItem", { field: p.label })}
          aria-expanded={asked}
          onClick={() => setAsked(!asked)}
        >
          <AddLinkIcon />
        </IconButton>
      ) : null}
      {editable && p.onUpload ? (
        <label
          title={uploading ? t("fields.uploading") : t("fields.upload")}
          class={`${iconButtonClass()} cursor-pointer focus-within:outline-2 focus-within:outline-blue-600`}
        >
          <UploadIcon />
          <input
            type="file"
            class="sr-only"
            multiple={p.many}
            disabled={uploading}
            aria-label={t("fields.uploadTo", { field: p.label })}
            onChange={(e) => {
              const files = [...(e.currentTarget.files ?? [])];
              e.currentTarget.value = "";
              if (files.length > 0) {
                upload(files);
              }
            }}
          />
        </label>
      ) : null}
      {listed ? (
        <IconButton label={t("items.view.showAs", { view: t(`items.view.${other}`) })} onClick={() => setView(other)}>
          {other === "table" ? <TableViewIcon /> : <CardsViewIcon />}
        </IconButton>
      ) : null}
    </span>
  );
  const add = searching ? (
    <Combobox
      label={p.label}
      placeholder={t("common.searchItems")}
      autoFocus={asked}
      search={(q, signal) => searchItems(p.space, q, signal, { entity: p.target ?? null })}
      onSelect={(o) => {
        setTitles({ ...titles, [o.value]: o.label });
        p.onChange(p.many ? [...p.ids.filter((x) => x !== o.value), o.value] : [o.value]);
        setAsked(false);
      }}
    />
  ) : null;
  const heading = <span class="font-medium text-zinc-800">{p.heading}</span>;
  return (
    <div class="flex flex-col gap-2">
      {listed && p.many ? (
        // Several, folded as a tree's group (short lists open) under its heading; its search under it, open or not.
        <Fold
          name={t("fields.linkedCount", { field: p.label })}
          label={heading}
          count={p.ids.length}
          extra={tools}
          toolsWhenOpen={true}
          under={add}
        >
          {() => linked}
        </Fold>
      ) : (
        <>
          <div class="flex min-h-7 items-center gap-2 text-sm">
            {heading}
            {tools}
          </div>
          {add}
          {listed ? linked : null}
        </>
      )}
      {!listed && p.ids.length > 0 && (
        <ul class="flex flex-wrap gap-2">
          {p.ids.map((id) => (
            <li key={id}>
              <Chip
                size="md"
                {...(editable
                  ? {
                      onRemove: () => p.onChange(p.ids.filter((x) => x !== id)),
                      removeLabel: t("fields.unlink", { title: title(id) }),
                    }
                  : {})}
              >
                {chip(id)}
              </Chip>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
